import { createRoute, useLoaderData } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { unwrapPageInitialData, type PageInitialData } from "~/app/api/pageData";
import { myAccountDashboardPageQueryOptions } from "~/app/api/pageData/myAccountDashboardPageQuery";
import type {
  MyAccountCustomerData,
  MyAccountAddressData,
  MyAccountDashboardPageInitialData,
  MyAccountSalesPartnerData,
  MyAccountSubsidiaryData,
} from "~/app/api/pageData/myAccountDashboardPageInitialData";
import { StorefrontShellFrame } from "~/app/shell/StorefrontShellFrame";
import { useStorefrontShellProps } from "~/app/router/storefrontShellPropsContext";
import { rootRoute } from "~/app/router/rootRoute";
import { createMyAccountPage } from "~/pages/MyAccountPage/MyAccountPage";
import type { MyAccountDataJSP } from "~/features/myAccount/profile/myAccountTypes";
import type { RootState } from "~/app/store";

const MY_ACCOUNT_PATH = "/my-account";

/** {@code StringUtils.defaultString} parity — null/undefined render as the empty string. */
const orEmpty = (value: string | null | undefined): string => value ?? "";

interface DashboardAffiliateContext {
  /** Redux {@code affiliate.savedSubsidiaryId} — hydrated from Session salesPartnerContext. */
  selectedSubsidiary: string;
  /** Redux {@code affiliate.visitedAffiliate} — hydrated from Session salesPartnerContext. */
  visitedAffiliate?: SalesPartnerDTO;
}

/**
 * Reproduces the legacy {@code MyAccountDashboardPageController#buildReactPageProps}
 * transform verbatim, including:
 *  - the {@code languageCode}→{@code language}, {@code vatNumber}→{@code vat},
 *    {@code additionalLine1}→{@code line2}, {@code postalCode}→{@code zipCode},
 *    {@code isocode}→{@code code} (languages) field remaps;
 *  - {@code StringUtils.defaultString} → {@code value ?? ""} for every nullable scalar
 *    (the JSP quoted every EL value, so nulls rendered as empty strings);
 *  - the address-null guards ({@code defaultShippingAddress == null ? "" : ...});
 *  - the 9 option-list adapters (titles/salutations/countries/industrySectors/jobTitles/
 *    functionalAreas/customerCategories/knowledgeAreas/languages);
 *  - the {@code affiliates} assembly: the facade-loaded {@code salesPartner} (transformed
 *    via the {@code SalesPartnerTransformer} mapping) merged with the Redux
 *    {@code visitedAffiliate}.
 *
 * Affiliate context ({@code selectedSubsidiary}, {@code visitedAffiliate}) is sourced from
 * the affiliate Redux slice, NOT plumbed through the page-data body — it already reaches
 * the SPA via the Session salesPartnerContext (verified: selected subsidiary
 * carries the identical value to the retired SCHA selected-subsidiary field).
 */
function toInputFieldValues(
  customer: MyAccountCustomerData,
  data: MyAccountDashboardPageInitialData,
  affiliate: DashboardAffiliateContext,
): MyAccountDataJSP["inputFieldValues"] {
  const address: MyAccountAddressData | undefined = customer.defaultShippingAddress;
  return {
    titleCode: orEmpty(customer.titleCode),
    salutationCode: orEmpty(customer.salutationCode),
    email: orEmpty(customer.email),
    firstName: orEmpty(customer.firstName),
    lastName: orEmpty(customer.lastName),
    industrySector: orEmpty(customer.industrySector),
    jobTitle: orEmpty(customer.jobTitle),
    functionalArea: orEmpty(customer.functionalArea),
    language: orEmpty(customer.languageCode),
    knowledgeArea: orEmpty(customer.knowledgeArea),
    customerCategory: orEmpty(customer.customerCategory),
    vat: orEmpty(customer.vatNumber),
    lmsRequest: customer.lmsRequest,
    company: orEmpty(address?.companyName),
    line1: orEmpty(address?.line1),
    line2: orEmpty(address?.additionalLine1),
    zipCode: orEmpty(address?.postalCode),
    city: orEmpty(address?.town),
    country: orEmpty(address?.country?.isocode),
    phone: orEmpty(address?.phone),
    selectedSubsidiary: orEmpty(affiliate.selectedSubsidiary),
    isSalesPartnerSaved: data.isSalesPartnerSaved ?? false,
  };
}

function toTitleOptions(items: { code: string; name?: string }[]): { code: string; name: string }[] {
  return items.map((item) => ({ code: item.code, name: item.name ?? "" }));
}

function toNamedCodeOptions(items: { code: string; name: string }[]): { code: string; name: string }[] {
  return items.map((item) => ({ code: item.code, name: item.name }));
}

function toIsocodeOptions(items: { isocode: string; name?: string }[]): { isocode: string; name: string }[] {
  return items.map((item) => ({ isocode: item.isocode, name: item.name ?? "" }));
}

function toMapOptions(source: Record<string, string>): { code: string; name: string }[] {
  return Object.entries(source).map(([key, value]) => ({ code: key, name: value }));
}

/**
 * Languages use {@code LanguageData.isocode} as the option code (Java {@code toOptions}
 * with {@code LanguageData::getIsocode} and codeKey {@code "code"} — remapping
 * {@code isocode}→{@code code}).
 */
function toLanguageOptions(items: { isocode: string; name?: string }[]): { code: string; name: string }[] {
  return items.map((item) => ({ code: item.isocode, name: item.name ?? "" }));
}

/**
 * Reproduces the legacy {@code SalesPartnerTransformer} mapping: {@code SubsidiaryDTO.id}→
 * {@code code}, {@code AddressData.townName}→{@code town}, and {@code country.name}→
 * {@code country} (string). Nullable fields are omitted to match the previous Jackson
 * NON_NULL serialisation, mirroring the reviewed {@code registeredUserCompleteRoute}
 * adapter. Country-name translation gap: the legacy builder resolved the country name
 * server-side via {@code schaefflerTranslationUtils.getTranslation(country.name)} (core
 * resource bundle). The frontend has no access to that bundle, so the raw key/name is
 * passed through unchanged — a known behavioural gap shared with the registered-user-complete
 * route.
 */
function toDashboardSalesPartner(salesPartner: MyAccountSalesPartnerData): SalesPartnerDTO {
  const result: Partial<SalesPartnerDTO> = {};
  if (salesPartner.affiliateId != null) {
    result.affiliateId = salesPartner.affiliateId;
  }
  if (salesPartner.logo != null) {
    result.logo = salesPartner.logo;
  }
  if (salesPartner.name != null) {
    result.name = salesPartner.name;
  }
  if (salesPartner.subsidiaries) {
    result.subsidiaries = salesPartner.subsidiaries.map(toDashboardSubsidiary) as SubsidiaryDTO[];
  }
  return result as SalesPartnerDTO;
}

function toDashboardSubsidiary(subsidiary: MyAccountSubsidiaryData): SubsidiaryDTO {
  const result: Partial<SubsidiaryDTO> = {};
  if (subsidiary.id != null) {
    result.code = subsidiary.id;
  }
  if (subsidiary.name != null) {
    result.name = subsidiary.name;
  }
  const address = subsidiary.address;
  if (address) {
    const addressResult: Partial<AddressDTO> = {};
    if (address.id != null) {
      addressResult.id = address.id;
    }
    if (address.line1 != null) {
      addressResult.line1 = address.line1;
    }
    if (address.line2 != null) {
      addressResult.line2 = address.line2;
    }
    if (address.townName != null) {
      addressResult.town = address.townName;
    }
    if (address.postalCode != null) {
      addressResult.postalCode = address.postalCode;
    }
    if (address.phone != null) {
      addressResult.phone = address.phone;
    }
    if (address.country?.name != null) {
      // SalesPartnerTransformer emits country as a translated string; the global AddressDTO
      // types it as { name, isocode }. Pre-existing contract looseness — cast carries the
      // faithful runtime shape.
      addressResult.country = address.country.name as unknown as { name: string; isocode: string };
    }
    result.address = addressResult as AddressDTO;
  }
  return result as SubsidiaryDTO;
}

export function toMyAccountProps(
  data: MyAccountDashboardPageInitialData | null | undefined,
  affiliate: DashboardAffiliateContext,
): MyAccountDataJSP {
  if (!data) {
    throw new Error("My-account page-data response is missing its required body");
  }
  const customer = data.customerData;

  const affiliates: SalesPartnerDTO[] = [];
  if (data.salesPartner) {
    affiliates.push(toDashboardSalesPartner(data.salesPartner));
  }
  if (affiliate.visitedAffiliate) {
    affiliates.push(affiliate.visitedAffiliate);
  }

  return {
    titles: toTitleOptions(data.titleData),
    salutations: toTitleOptions(data.salutationData),
    countries: toIsocodeOptions(data.countryData),
    industrySectors: toMapOptions(data.industrySectorData),
    jobTitles: toMapOptions(data.jobTitleData),
    functionalAreas: toMapOptions(data.functionalAreaData),
    customerCategories: toNamedCodeOptions(data.customerCategoryData),
    knowledgeAreas: toNamedCodeOptions(data.knowledgeAreaData),
    languages: toLanguageOptions(data.languagesData),
    inputFieldValues: toInputFieldValues(customer, data, affiliate),
    newsletterOne: data.newsletterOne,
    privacyPolicy: data.privacyPolicy,
    profileCenterLink: orEmpty(data.profileCenterLink),
    profileCenterEmailLink: orEmpty(data.profileCenterEmailLink),
    affiliates,
  } as MyAccountDataJSP;
}

function MyAccountDashboardRoute() {
  const result = useLoaderData({
    from: MY_ACCOUNT_PATH,
  }) as PageInitialData<MyAccountDashboardPageInitialData>;
  const shellProps = useStorefrontShellProps();
  // Affiliate context reaches the SPA via the Session-hydrated affiliate Redux slice,
  // not via the page-data body. Selected subsidiary carries the same value as the legacy
  // SessionData.salesPartnerContext.preferred.selectedSubsidiaryId.
  const selectedSubsidiary = useSelector((state: RootState) => state.affiliate?.savedSubsidiaryId) ?? "";
  const visitedAffiliate = useSelector((state: RootState) => state.affiliate?.visitedAffiliate);

  return (
    <StorefrontShellFrame variant={shellProps.variant} metadata={result.metadata}>
      {createMyAccountPage(toMyAccountProps(result.data, { selectedSubsidiary, visitedAffiliate }))}
    </StorefrontShellFrame>
  );
}

export async function loadMyAccountDashboardPageData(context: { queryClient: QueryClient }) {
  const result = await context.queryClient.ensureQueryData(myAccountDashboardPageQueryOptions());

  return unwrapPageInitialData(result);
}

export const myAccountDashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: MY_ACCOUNT_PATH,
  loader: async ({ context }) => loadMyAccountDashboardPageData(context),
  component: MyAccountDashboardRoute,
});
